import { useEffect, useMemo, useState } from "react";
import { DiscoveryStats } from "./discovery-stats";
import { LinkActivity } from "./link-activity";
import useRequest from "../../../components/hooks/use-req";
import {
  DiscoveryActivityItem,
  DiscoveryOverviewResponse,
  DiscoverySort,
  DiscoveryStatusFilter,
} from "./types";
import { buildPeriodRequestParams, DateRange, EMPTY_DATE_RANGE, PeriodValue } from "../../../components/period-filter";

// Discovery Links — backed by GET /super-admin/discovery-links/overview (both the
// performance stat cards and the activity table come from this one response; no mock data).
const DiscoveryLinks: React.FC = () => {
  const [period, setPeriod] = useState<PeriodValue>("all_time");
  const [customRange, setCustomRange] = useState<DateRange>(EMPTY_DATE_RANGE);
  const [sort, setSort] = useState<DiscoverySort>("earnings_desc");
  const [statusFilter, setStatusFilter] = useState<DiscoveryStatusFilter>("all");
  const [overview, setOverview] = useState<DiscoveryOverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const authToken = localStorage.getItem("authToken");

  const { makeRequest: getDiscoveryOverview } = useRequest("/super-admin/discovery-links/overview", "GET", {
    Authorization: `Bearer ${authToken}`,
  });

  useEffect(() => {
    const params = buildPeriodRequestParams(period, customRange, { omitAllTime: true });
    if (!params) return;

    const fetchOverview = async () => {
      setLoading(true);
      try {
        const [response] = await getDiscoveryOverview(undefined, params);
        setOverview(response?.status ? response : null);
      } finally {
        setLoading(false);
      }
    };
    fetchOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, customRange]);

  // Local-only until enable/disable has a confirmed endpoint to persist against — the
  // overview response doesn't return one, so this just reflects in the UI for now.
  const [localActiveOverrides, setLocalActiveOverrides] = useState<Record<number, boolean>>({});

  const toggleActive = (id: number) => {
    setLocalActiveOverrides((prev) => ({
      ...prev,
      [id]: !(prev[id] ?? overview?.activity.find((a) => a.id === id)?.is_active),
    }));
  };

  const activity: DiscoveryActivityItem[] = useMemo(
    () =>
      (overview?.activity || []).map((item) => ({
        ...item,
        is_active: localActiveOverrides[item.id] ?? item.is_active,
      })),
    [overview, localActiveOverrides]
  );

  const visibleLinks = useMemo(() => {
    const filtered =
      statusFilter === "all" ? activity : activity.filter((l) => (statusFilter === "active" ? l.is_active : !l.is_active));

    return [...filtered].sort((a, b) => {
      if (sort === "earnings_desc") return b.earnings - a.earnings;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [activity, statusFilter, sort]);

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-xl lg:text-[28px] flex gap-2 text-black">Discovery Links</h1>
        <p className="text-[#717171] text-[14px] lg:text-[18px]">
          Monitor VouchLinks created for products and businesses outside active partner programs.
        </p>
      </div>

      <DiscoveryStats
        performance={overview?.performance ?? null}
        loading={loading}
        period={period}
        onPeriodChange={setPeriod}
        customRange={customRange}
        onCustomRangeChange={setCustomRange}
      />

      <div className="mt-10">
        <LinkActivity
          items={visibleLinks}
          loading={loading}
          sort={sort}
          onSortChange={setSort}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          period={period}
          onPeriodChange={setPeriod}
          customRange={customRange}
          onCustomRangeChange={setCustomRange}
          onToggleActive={toggleActive}
        />
      </div>
    </div>
  );
};

export default DiscoveryLinks;
